<template>
  <div
    class="attachment-component"
    :class="[`attachment-component_${attachmentType}`]"
  >
    <div class="attachment-component__content">
      <div
        v-if="isImage && !deletedAttachment"
        class="attachment-component__content-in"
        :style="{ width: width + 'px', height: height + 'px' }"
      >
        <div
          class="attachment-component__drag-handle"
          contenteditable="false"
          draggable="true"
          data-drag-handle
        >
          <img
            :src="require('@/assets/images/icons/drag.svg')"
            width="10"
            height="10"
          />
        </div>
        <Skeletor
          v-if="node?.attrs?.isSkeletor"
          :width="`${width}px`"
          :height="`${height}px`"
        />
        <VueDraggableResizable
          v-else-if="width && height"
          :ref="'resizable_' + attachmentId"
          :w="width"
          :h="height"
          :draggable="false"
          :handles="['br']"
          :parent="false"
          :lockAspectRatio="true"
          :maxWidth="maxWidth"
          :maxHeight="maxHeight"
          :minWidth="100"
          :minHeight="100"
          @resizing="onResize"
        >
          <ImageItem
            :attachment="initFileInstance"
            :blobUrl="blobUrl"
            :fancyboxData="`editor_fancybox_${projectId}`"
            :fileMetadataId="attachmentId"
            :loadFullSize="true"
          />
        </VueDraggableResizable>
      </div>
      <div
        v-else-if="isImage"
        class="attachment-component__content-in"
        :style="{ width: '290px', height: '200px' }"
      >
        <ImageItem
          :attachment="deletedAttachment"
          :blobUrl="blobUrl"
        />
      </div>
      <VideoAttachmentItem
        v-else-if="isVideo"
        :key="currentVideoUrl"
        :file="deletedAttachment ? deletedAttachment : initFileInstance"
        :videoPath="currentVideoUrl"
        :fancyboxData="`editor_fancybox_${projectId}`"
        :useSharedFancybox="true"
        @click="updateVideoLink"
      />
      <FileItem
        v-else-if="isFile"
        :key="attachmentId"
        :file="deletedAttachment ? deletedAttachment : initFileInstance"
        fileMarkup="line"
        class="custom-file_reference"
        :removeButton="true"
      />
      <div
        v-else
        class="attachment-component__link"
        :tabindex="-1"
        @click.prevent="onDownload"
      >
        {{ attachmentName }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import VueDraggableResizable from "vue-draggable-resizable/src/components/vue-draggable-resizable.vue"
import { Prop } from "vue-property-decorator"
import { Action, Getter } from "s-vuex-class"

import { Actions } from "@/store/actions"
import { Getters } from "@/store/getters"

import "vue-draggable-resizable/src/components/vue-draggable-resizable.css"

import { Skeletor } from "vue-skeletor"
import { Editor } from "@tiptap/core"
import { Node as ProseMirrorNode } from "@tiptap/pm/model"

import FileItem from "@/components/File/FileItemSimplified.vue"
import ImageItem from "@/components/File/ImageItem.vue"
import VideoAttachmentItem from "@/components/VideoPlayer/VideoAttachmentItem.vue"

import { FileMetaData, Item, Project } from "@/models"
import { ReferenceType } from "@/plugins/extensions/ReferenceType"
import { errorHandlerService } from "@/services/error-handler.service"
import { getImageSize } from "@/utils/helpers"

@Options({
  name: "Attachment",
  components: {
    VideoAttachmentItem,
    VueDraggableResizable,
    FileItem,
    Skeletor,
    ImageItem
  }
})
export default class Attachment extends Vue {
  @Prop() node: ProseMirrorNode
  @Prop() editor: Editor
  @Prop() updateAttributes: (attributes: Record<string, any>) => void
  @Prop() getPos: () => number

  @Getter(Getters.ATTACHMENT_UPLOAD) readonly attachmentIds: string[]
  @Getter(Getters.GET_PROJECT) readonly project: Project
  @Getter(Getters.GET_ITEM) readonly item: Item
  @Action(Actions.ATTACHMENT_VIEW) loadFile: ({
    fileMetadataId,
    fileName,
    fullSize
  }: {
    fileMetadataId: string
    fileName?: string
    fullSize?: boolean
  }) => Promise<Blob>
  @Action(Actions.ATTACHMENT_DOWNLOAD) downloadFile: ({
    fileMetadataId,
    fileName
  }: {
    fileMetadataId: string
    fileName: string
  }) => void
  @Action(Actions.ATTACHMENT_VIDEO_URL) videoUrl: ({
    projectId,
    attachmentId
  }: {
    projectId?: string
    attachmentId: string
  }) => Promise<string>

  maxWidth = 1000
  maxHeight = 1000
  blobUrl = ""
  width = 0
  height = 0
  pos: number | undefined = undefined
  currentVideoUrl = ""

  get projectId() {
    return this.project?.id ?? ""
  }

  get isImage() {
    return this.initFileInstance.isImage || this.node.attrs.type === "img"
  }

  get isVideo() {
    return this.initFileInstance.isVideo || this.node.attrs.type === "video"
  }

  get isFile() {
    return (
      this.node.attrs.configType === ReferenceType.FILE ||
      this.node.attrs.type === "file" ||
      (!this.isImage && !this.isVideo)
    )
  }

  get attachmentType() {
    if (this.isImage) return "img"
    return "a"
  }

  get attachmentId() {
    return this.initFileInstance.id || this.node.attrs.id
  }

  get attachmentName() {
    return this.initFileInstance.name
  }

  get attachmentWaitKey() {
    return `get.attachment.${this.attachmentId || "unknown"}`
  }

  get info() {
    try {
      return JSON.parse(this.node.attrs.info)
    } catch {
      return false
    }
  }

  get deletedAttachment() {
    if (this.item.croppedPreview) {
      return { ...this.initFileInstance, deleted: true }
    }
    return this.item.deletedFileMetadata?.find(f => f.id === this.attachmentId)
  }

  get initFileInstance() {
    return FileMetaData.create(this.info)
  }

  revokeObjectURL() {
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
  }

  onResize(_x, _y, width, height) {
    this.width = this.roundToEven(width)
    this.height = this.roundToEven(height)

    this.updateAttributes({
      width: this.width,
      height: this.height,
      customResize: true
    })

    this.editor.commands.selectNodeBackward()
  }

  onDownload() {
    this.downloadFile({
      fileMetadataId: this.attachmentId,
      fileName: this.attachmentName
    })
  }

  async created() {
    if (this.deletedAttachment) return
    this.updateVideoLink()
  }

  async updateVideoLink() {
    this.currentVideoUrl = await this.videoUrl({
      projectId: this.projectId,
      attachmentId: this.attachmentId
    })
  }

  async mounted() {
    if (!this.node) {
      errorHandlerService.customLogError(`Editor node: ${this.node}`)
    }
    if (!this.editor) {
      errorHandlerService.customLogError(`Editor data: ${this.editor}`)
    }
    await this.initFile()
  }

  async initFile() {
    if (!this.isImage || !this.attachmentId || this.deletedAttachment) {
      this.$wait.end(this.attachmentWaitKey)
      return
    }

    if (this.node?.attrs?.isSkeletor) {
      this.width = 300
      this.height = 100
      this.updateAttributes({ width: this.width, height: this.height })
      this.$wait.start(this.attachmentWaitKey)
      return
    }
    this.$wait.start(this.attachmentWaitKey)

    const blob: Blob = await this.loadFile({
      fileMetadataId: this.attachmentId
    })

    if (!blob) {
      this.$wait.end(this.attachmentWaitKey)
      return
    }

    this.blobUrl = URL.createObjectURL(blob)

    if (!this.blobUrl) {
      this.$wait.end(this.attachmentWaitKey)
      return
    }

    /**
     * Existing attachments already have persisted dimensions and must not update the editor on load.
     * Only newly uploaded images need an attribute transaction to save their calculated aspect ratio.
     */
    if (!this.$wait.is(this.attachmentWaitKey) || !this.attachmentIds.includes(this.attachmentId)) {
      this.width = this.node.attrs.width
      this.height = this.node.attrs.height
      this.$wait.end(this.attachmentWaitKey)
      return
    }

    const { width, height } = await getImageSize(this.blobUrl)

    if (this.getPos) {
      this.pos = this.getPos()
    } else {
      errorHandlerService.customLogError(`File position method: ${this.getPos}`)
    }

    if (this.node.attrs.customResize) {
      this.width = this.node.attrs.width
      this.height = this.node.attrs.height
      this.maxWidth = width
      this.maxHeight = height
    } else if (width > height) {
      this.width = this.node.attrs.width
      this.height = this.roundToEven((this.width * height) / width)
      this.maxWidth = width
    } else {
      this.height = this.node.attrs.height
      this.width = this.roundToEven((this.height * width) / height)
      this.maxHeight = height
    }

    const widthChanged = this.width !== this.node.attrs.width
    const heightChanged = this.height !== this.node.attrs.height
    if (this.getPos && this.getPos() && (widthChanged || heightChanged)) {
      this.updateAttributes({ width: this.width, height: this.height })
    }
    this.$wait.end(this.attachmentWaitKey)
  }

  roundToEven(number) {
    return Math.round(number / 2) * 2
  }
}
</script>
